import React, { useEffect, useState } from "react";
import { Radio } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, MaskLine } from "../../lib/motion.jsx";
import { getSportMatches, getSportTeams } from "../../lib/api";
import { MatchRow } from "./SportBits";

const CAT_ORDER = { senioren: 0, veteranen: 1, jeugd: 2 };

export default function SportWedstrijden({ sport }) {
  const { t, lang } = useLang();
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [tab, setTab] = useState("upcoming");
  const [matches, setMatches] = useState(null);

  const sportLabel = t(sport === "futsal" ? "nav_futsal" : "nav_veldvoetbal");

  useEffect(() => {
    setTeamId("");
    setTab("upcoming");
    getSportTeams(sport).then((ts) =>
      setTeams([...ts].sort((a, b) => (CAT_ORDER[a.category] ?? 9) - (CAT_ORDER[b.category] ?? 9) || a.name.localeCompare(b.name)))
    );
  }, [sport]);

  useEffect(() => {
    setMatches(null);
    getSportMatches(sport, {
      team_id: teamId || undefined,
      status: tab === "all" ? undefined : tab,
    }).then(setMatches);
  }, [sport, teamId, tab]);

  const tabs = [
    { id: "upcoming", label: t("upcoming") },
    { id: "results", label: t("resultsTab") },
    { id: "all", label: t("all") },
  ];

  return (
    <div className="fc-theme" data-testid={`sport-wedstrijden-${sport}`}>
      <section className="border-b border-white/10 bg-gradient-to-b from-[#141414] to-[#0A0A0A] pt-24 pb-14">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-4 flex items-center gap-2">
            <Radio size={16} className="text-[#CCFF00]" />
            <span className="text-xs uppercase tracking-widest text-white/50">FC Inkad · {sportLabel} · {t("officialData")}</span>
          </div>
          <h1 className="font-anton text-6xl md:text-8xl uppercase leading-none"><MaskLine>{t("nav_wedstrijden")}</MaskLine></h1>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-12 space-y-8">
        {/* Team filter */}
        {teams.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="sport-team-filters">
            <button onClick={() => setTeamId("")} data-testid="sport-team-filter-all"
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${!teamId ? "border-[#CCFF00] bg-[#CCFF00] text-black" : "border-white/15 text-white/60 hover:border-white/40"}`}>
              {t("allTeams")}
            </button>
            {teams.map((tm) => (
              <button key={tm.external_id} onClick={() => setTeamId(tm.external_id)} data-testid={`sport-team-filter-${tm.external_id}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${teamId === tm.external_id ? "border-[#CCFF00] bg-[#CCFF00] text-black" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                {tm.name}
              </button>
            ))}
          </div>
        )}

        {/* Status tabs */}
        <div className="flex gap-1 border-b border-white/10" data-testid="sport-status-tabs">
          {tabs.map((x) => (
            <button key={x.id} onClick={() => setTab(x.id)} data-testid={`sport-tab-${x.id}`}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wide transition-colors ${tab === x.id ? "border-b-2 border-[#CCFF00] text-[#CCFF00]" : "text-white/50 hover:text-white"}`}>
              {x.label}
            </button>
          ))}
        </div>

        {/* Matches */}
        <div className="space-y-3" data-testid="sport-matches-list">
          {matches === null && <p className="text-white/40 text-sm">…</p>}
          {matches && matches.length === 0 && <p className="text-white/40" data-testid="sport-no-matches">{t("noMatches")}</p>}
          {matches && matches.map((m, i) => (
            <Reveal key={m.external_id} delay={Math.min(i * 0.03, 0.3)}>
              <MatchRow m={m} t={t} lang={lang} />
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
